import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { HeadingType, HeadingWeight } from '@freelancer/ui/heading';
import { LinkColor, QueryParams } from '@freelancer/ui/link';
import { Margin } from '@freelancer/ui/margin';
import { FontColor, FontType, FontWeight, TextSize } from '@freelancer/ui/text';

export enum UsernameSize {
  SMALL = 'small',
  MID = 'mid',
  LARGE = 'large',
  XLARGE = 'xlarge',
  XXLARGE = 'xxlarge',
}

export enum UsernameColor {
  DARK = 'dark',
  LIGHT = 'light',
  INHERIT = 'inherit',
}

@Component({
  selector: 'fl-username',
  template: `
    <ng-container *ngIf="!isLink">
      <ng-container *ngIf="!isHeading">
        <fl-bit class="Username-row" [attr.data-truncate]="truncateText">
          <fl-flag
            *ngIf="country"
            class="Username-flag"
            [attr.data-size]="size"
            [country]="country"
            [flMarginRight]="Margin.XXSMALL"
          ></fl-flag>
          <fl-text
            *ngIf="displayName"
            class="Username-displayName"
            [attr.data-truncate]="truncateText"
            [color]="displayNameColor"
            [flMarginRight]="compact ? Margin.NONE : Margin.XXXSMALL"
            [fontType]="FontType.SPAN"
            [size]="usernameSize"
            [weight]="FontWeight.INHERIT"
          >
            {{ displayName }}
          </fl-text>
        </fl-bit>
        <fl-text
          *ngIf="showUsernameTag"
          class="Username-userId"
          [attr.data-truncate]="truncateText"
          [attr.data-size]="size"
          [color]="usernameColor"
          [fontType]="FontType.SPAN"
          [size]="usernameSize"
          [weight]="FontWeight.INHERIT"
        >
          <ng-container *ngTemplateOutlet="userhandle"></ng-container>
        </fl-text>
      </ng-container>
      <ng-container *ngIf="isHeading">
        <fl-bit class="Username-row">
          <fl-flag
            *ngIf="country"
            class="Username-flag"
            [attr.data-size]="size"
            [country]="country"
            [flMarginRight]="Margin.XXSMALL"
          ></fl-flag>
          <fl-heading
            *ngIf="displayName"
            class="Username-displayName"
            [attr.data-truncate]="truncateText"
            [color]="displayNameColor"
            [flMarginRight]="compact ? Margin.NONE : Margin.XXXSMALL"
            [headingType]="HeadingType.H3"
            [size]="usernameSize"
            [weight]="HeadingWeight.INHERIT"
          >
            {{ displayName }}
          </fl-heading>
        </fl-bit>
        <fl-heading
          *ngIf="showUsernameTag"
          class="Username-userId"
          [attr.data-truncate]="truncateText"
          [attr.data-size]="size"
          [color]="usernameColor"
          [headingType]="HeadingType.H3"
          [size]="usernameSize"
          [weight]="HeadingWeight.INHERIT"
        >
          <ng-container *ngTemplateOutlet="userhandle"></ng-container>
        </fl-heading>
      </ng-container>
    </ng-container>
    <ng-container *ngIf="isLink">
      <fl-bit class="Username-row" [attr.data-truncate]="truncateText">
        <fl-flag
          *ngIf="country"
          class="Username-flag"
          [attr.data-size]="size"
          [country]="country"
          [flMarginRight]="Margin.XXSMALL"
        ></fl-flag>
        <fl-text
          *ngIf="displayName"
          class="Username-displayName"
          [color]="displayNameColor"
          [attr.data-truncate]="truncateText"
        >
          <fl-link
            class="Username-displayName-link"
            [color]="LinkColor.INHERIT"
            [flMarginRight]="compact ? Margin.NONE : Margin.XXXSMALL"
            [link]="link"
            [newTab]="newTab"
            [size]="usernameSize"
            [queryParams]="queryParams"
          >
            {{ displayName }}
          </fl-link>
        </fl-text>
      </fl-bit>
      <fl-text
        [color]="usernameColor"
        class="Username-userId"
        [attr.data-truncate]="truncateText"
      >
        <fl-link
          *ngIf="showUsernameTag"
          [attr.data-size]="size"
          [color]="FontColor.INHERIT"
          [link]="link"
          [newTab]="newTab"
          [size]="usernameSize"
          [queryParams]="queryParams"
        >
          <ng-container *ngTemplateOutlet="userhandle"></ng-container>
        </fl-link>
      </fl-text>
    </ng-container>
    <ng-template #userhandle>{{ '@' + username }}</ng-template>
  `,
  styleUrls: ['./username.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UsernameComponent implements OnChanges, OnInit {
  FontColor = FontColor;
  TextSize = TextSize;
  FontType = FontType;
  FontWeight = FontWeight;
  HeadingType = HeadingType;
  HeadingWeight = HeadingWeight;
  LinkColor = LinkColor;
  Margin = Margin;
  UsernameSize = UsernameSize;

  displayNameColor: FontColor;
  usernameColor: FontColor;
  usernameSize: TextSize;
  isHeading = false;

  @Input() color = UsernameColor.DARK;
  @Input() displayName: string;
  @Input() username: string;
  @Input() country?: string;
  @Input() queryParams?: QueryParams;
  @Input() link?: string;
  get isLink() {
    return this.link !== undefined ? this.link.length > 0 : false;
  }
  @Input() newTab?: boolean;
  @Input() truncateText?: boolean;

  @Input() size = UsernameSize.MID;

  @HostBinding('attr.data-compact')
  @Input()
  compact = false;

  /** whether to show the @userame or not */
  @Input() showUsernameTag? = true;

  ngOnInit() {
    this.initializeUsername();
  }

  ngOnChanges(changes: SimpleChanges) {
    if ('color' in changes) {
      this.setUsernameColor();
    }

    if ('size' in changes) {
      this.setUsernameSize();
    }
  }

  initializeUsername() {
    this.setUsernameColor();
    this.setUsernameSize();
  }

  setUsernameColor() {
    switch (this.color) {
      case UsernameColor.LIGHT:
        this.displayNameColor = FontColor.LIGHT;
        this.usernameColor = FontColor.LIGHT;
        break;
      case UsernameColor.INHERIT:
        this.displayNameColor = FontColor.INHERIT;
        this.usernameColor = FontColor.INHERIT;
        break;
      default:
        this.displayNameColor = FontColor.DARK;
        this.usernameColor = FontColor.MID;
    }
  }

  setUsernameSize() {
    switch (this.size) {
      case UsernameSize.XXLARGE:
        this.isHeading = true;
        this.usernameSize = TextSize.LARGE;
        break;
      case UsernameSize.XLARGE:
        this.isHeading = true;
        this.usernameSize = TextSize.MID;
        break;
      case UsernameSize.LARGE:
        this.isHeading = false;
        this.usernameSize = TextSize.SMALL;
        break;
      case UsernameSize.SMALL:
        this.isHeading = false;
        this.usernameSize = TextSize.XXSMALL;
        break;
      default:
        this.isHeading = false;
        this.usernameSize = TextSize.XSMALL;
    }
  }
}
